const Namespace = {
	UI: 'ui',
	API: 'api',
	LOGIN: 'login',
	LOGOUT: 'logout',
	GET_LOGIN_STATUS: 'getLoginStatus',
	GET_AUTH_RESPONSE: 'getAuthResponse',
};

const Method = {
	GET: 'get',
	POST: 'post',
	DELETE: 'delete',
};

const LoginStatus = {
	CONNECTED: 'connected',
	AUTHORIZATION_EXPIRED: 'authorization_expired',
	NOT_AUTHORIZED: 'not_authorized',
};

class FBError extends Error {
	/**
	 * @param {string} message
	 * @param code
	 * @param type
	 */

	constructor(message, code, type) {
		super(message);
		this.code = code;
		this.type = type;
	}
}

class Facebook {
	/**
	 * @var {facebook.InitParams}
	 */
	#options;

	#loadingPromise;

	/**
	 * @param {Partial<facebook.InitParams>} options
	 */
	constructor(options = {}) {
		if (!options.appId) {
			throw new Error('You need to set appId');
		}

		this.#options = {
			version: 'v20.0',
			autoLogAppEvents: true,
			xfbml: true,
			cookie: true,
			status: true,
			frictionlessRequests: true,
			...options,
		};
	}

	/**
	 * @returns {Promise<*>}
	 */
	async init() {
		if (this.#loadingPromise) {
			return this.#loadingPromise;
		}

		this.#loadingPromise = new Promise((resolve) => {
			window.fbAsyncInit = () => {
				window.FB.init(this.#options);

				resolve(this);
			};

			if (window.document.getElementById('facebook-jssdk')) {
				return resolve(this);
			}

			const js = window.document.createElement('script');
			js.id = 'facebook-jssdk';
			js.async = true;
			js.defer = true;
			js.crossOrigin = 'anonymous';
			js.src = 'https://connect.facebook.net/en_US/sdk.js';

			window.document.body.appendChild(js);
		});

		return this.#loadingPromise;
	}

	/**
	 * @returns {facebook.FacebookStatic}
	 */
	getFB() {
		return window.FB;
	}

	/**
	 * @param {string} namespace
	 * @param {*[]} before
	 * @param {*[]} after
	 * @returns {Promise<*>}
	 */
	async #process(namespace, before = [], after = []) {
		await this.init();
		const fb = this.getFB();

		if (typeof fb[namespace] !== 'function') {
			throw new Error(`FB.${namespace} is not a function`);
		}
		return new Promise((resolve, reject) => {
			fb[namespace](
				...before,
				(response) => {
					if (!response) {
						if (namespace === Namespace.UI) return;
						reject(new Error('Response is undefined'));
					} else if (!!response && 'error' in response) {
						const { code, type, message } = response.error;
						reject(new FBError(message, code, type));
					} else {
						resolve(response);
					}
				},
				...after
			);
		});
	}

	/**
	 * @param {TFbUiParams} options
	 * @returns {Promise<*>}
	 */
	async ui(options) {
		return this.#process(Namespace.UI, [options]);
	}

	/**
	 * @param {string} path
	 * @param {string} method
	 * @param params
	 * @returns {Promise<*>}
	 */
	async api(path, method = Method.GET, params = {}) {
		return this.#process(Namespace.API, [path, method, params]);
	}

	/**
	 * @param {TFbMeGetParams} params
	 * @returns {Promise<TFbMeResponse>}
	 */
	async getProfile(params) {
		return this.api('/me', Method.GET, params);
	}

	/**
	 * @param {facebook.StatusResponse|undefined} loginResponse
	 * @returns {Promise<facebook.AuthResponse>}
	 */
	async getTokenDetail(loginResponse = undefined) {
		if (loginResponse?.status === LoginStatus.CONNECTED) {
			return loginResponse.authResponse;
		}

		const response = await this.getLoginStatus();
		if (response.status === LoginStatus.CONNECTED) {
			return response.authResponse;
		}
		throw new Error('Token is undefined');
	}

	/**
	 * @param params
	 * @param {facebook.StatusResponse|undefined} response
	 * @returns {Promise<{profile: TFbMeResponse, tokenDetail: facebook.AuthResponse}>}
	 */
	async getTokenDetailWithProfile(params, response) {
		const tokenDetail = await this.getTokenDetail(response);
		const profile = await this.getProfile(params);
		return {
			profile,
			tokenDetail,
		};
	}

	/**
	 * @returns {Promise<string>}
	 */
	async getToken() {
		const authResponse = await this.getTokenDetail();
		return authResponse.accessToken;
	}

	/**
	 * @returns {Promise<string>}
	 */
	async getUserId() {
		const authResponse = await this.getTokenDetail();
		return authResponse.userID;
	}

	/**
	 * @param {string} to
	 * @param {facebook.GameRequestDialogParams} options
	 * @returns {Promise<*>}
	 */
	async sendInvite(to, options) {
		return this.ui(Object.assign({ to, method: 'apprequests' }, options));
	}

	async postAction(
		ogNamespace,
		ogAction,
		ogObject,
		ogObjectUrl,
		noFeedStory = false
	) {
		let url = `/me/${ogNamespace}:${ogAction}?${ogObject}=${encodeURIComponent(ogObjectUrl)}`;
		if (noFeedStory === true) {
			url += '&no_feed_story=true';
		}
		return this.api(url, Method.POST);
	}

	/**
	 * @returns {Promise<*>}
	 */
	async getPermissions() {
		const response = await this.api('/me/permissions');
		return response.data;
	}

	/**
	 * @param {string[]} permissions
	 * @returns {Promise<boolean>}
	 */
	async hasPermissions(permissions) {
		const usersPermissions = await this.getPermissions();
		const findePermissions = permissions.filter((p) => {
			const currentPermission = usersPermissions.find((row) => {
				const { permission, status } = row;
				return status === 'granted' && permission === p;
			});
			return !!currentPermission;
		});
		return findePermissions.length === permissions.length;
	}

	/**
	 * @param {facebook.FacebookEventType} eventName
	 * @param callback
	 * @returns {Promise<function(): Promise<void>>}
	 */
	async subscribe(eventName, callback) {
		await this.init();
		this.getFB().Event.subscribe(eventName, callback);
		return () => this.unsubscribe(eventName, callback);
	}

	/**
	 * @param {facebook.FacebookEventType} eventName
	 * @param {(response: facebook.StatusResponse) => void} callback
	 */
	async unsubscribe(eventName, callback) {
		await this.init();
		this.getFB().Event.unsubscribe(eventName, callback);
	}

	/**
	 * @param {HTMLElement|undefined} parentNode
	 * @returns {Promise<void>}
	 */
	async parse(parentNode) {
		await this.init();
		if (typeof parentNode === 'undefined') {
			this.getFB().XFBML.parse();
		} else {
			this.getFB().XFBML.parse(parentNode);
		}
	}

	async getRequests() {
		return this.api('/me/apprequests');
	}

	async removeRequest(requestId) {
		return this.api(requestId, Method.DELETE);
	}

	async paySimple(product, quantity = 1) {
		return this.ui({
			method: 'pay',
			action: 'purchaseitem',
			product,
			quantity,
		});
	}

	async pay(product, options) {
		return this.ui(
			Object.assign({ method: 'pay', action: 'purchaseitem', product }, options)
		);
	}

	/**
	 * @param {facebook.LoginOptions|undefined} options
	 * @returns {Promise<facebook.StatusResponse>}
	 */
	async login(options = {}) {
		return this.#process(
			Namespace.LOGIN,
			[],
			[
				{
					scope: 'public_profile,email',
					return_scopes: true,
					...options,
				},
			]
		);
	}

	/**
	 * @returns {Promise<facebook.StatusResponse>}
	 */
	async getLoginStatus() {
		return this.#process(Namespace.GET_LOGIN_STATUS);
	}

	async getAuthResponse() {
		return this.#process(Namespace.GET_AUTH_RESPONSE);
	}

	/**
	 * @returns {Promise<facebook.StatusResponse>}
	 */
	async logout() {
		return this.#process(Namespace.LOGOUT);
	}
}

const fb = new Facebook({
	appId: isHofProd ? '134672596609680' : '765253297254140',
});

await fb.init();

export default fb;
